export type PapPdfOutcome =
	| { kind: "none" }
	| { kind: "language" }
	| { kind: "fixed"; pdftype: number };

const AMGEN_PHARMACY = [
	"prolia",
	"enbrel",
	"corlanor",
	"repatha",
	"sensipar",
	"aimovig",
	"amjevita",
	"otezla",
];

const ABBVIE = ["humira", "botox", "skyrizi"];

const GILEAD = [
	"veklury",
	"remdesivir",
	"biktarvy",
	"complera",
	"emtriva",
	"genvoya",
	"odefsey",
	"stribild",
	"sunlenca",
	"truvada",
	"descovy",
	"tybost",
	"viread",
	"atripla",
	"harvoni",
	"epclusa",
	"hepsera",
	"sovaldi",
	"vemlidy",
	"vosevi",
	"tecartus",
	"trodelvy",
	"yescarta",
	"zydelig",
	"ambisome",
	"cayston",
	"letairis",
	"ranexa",
	"tamiflu",
];

const JANSSEN = [
	"balversa",
	"darzalex",
	"darzalex faspro",
	"edurant",
	"elmiron",
	"erleada",
	"infliximab",
	"intelence",
	"invega hafyera",
	"invega sustenna",
	"invega trinza",
	"invokamet",
	"invokamet xr",
	"invokana",
	"ponvory",
	"prezcobix",
	"prezista",
	"remicade",
	"risperdal consta",
	"rybrevant",
	"simponi",
	"simponi aria",
	"spravato",
	"stelara",
	"symtuza",
	"tecvayli",
	"tremfya",
	"xarelto",
	"yondelis",
];

const LILLY = [
	"verzenio",
	"cialis",
	"cymbalta",
	"evista",
	"forteo",
	"prozac",
	"basaglar",
	"emgality",
	"humalog",
	"humulin",
	"lyumjev",
	"reyvow",
	"trulicity",
	"humatrope",
	"omvoh",
	"olumiant",
	"taltz",
	"alimta",
	"cyramza",
	"erbitux",
	"jaypirca",
	"portrazza",
	"retevmo",
];

/** Manufacturer templates resolved by medication short name under member-case pdfType 6. */
const TYPE6_PAP = [
	"adakveo",
	"adbry",
	"adstiladrin",
	"actemra",
	"alecensa",
	"apretude",
	"arikayce",
	"avonex",
	"bafiertam",
	"benlysta",
	"besremi",
	"bimzelx",
	"cimzia",
	"cosentyx",
	"creon",
	"cresemba",
	"doptelet",
	"dovato",
	"entyvio",
	"hadlima",
	"hemlibra",
	"iluvien",
	"inqovi",
	"jakafi",
	"jubbonti",
	"kesimpta",
	"kevzara",
	"keytruda",
	"kisqali",
	"kovaltry",
	"litfulo",
	"lupron",
	"nemluvio",
	"ogivri",
	"opdivo",
	"opzelura",
	"orencia",
	"orgovyx",
	"perjeta",
	"praluent",
	"revlimid",
	"rinvoq",
	"rituxan",
	"somatuline",
	"strensiq",
	"tagrisso",
	"takhzyro",
	"tezspire",
	"tyenne",
	"tymlos",
	"vabysmo",
	"venclexta",
	"venofer",
	"voyxact",
	"vyepti",
	"vyvgart hytrulo",
	"vyvgart",
	"xolair",
	"xtandi",
	"xywav",
	"zarxio",
	"zeposia",
	"zoladex",
	"zymfentra",
];

function includesAny(haystack: string, needles: readonly string[]): boolean {
	const hay = haystack.toLowerCase();
	return needles.some((needle) => hay.includes(needle.toLowerCase()));
}

function equalsIgnoreCase(a: string, b: string): boolean {
	return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function scalarToString(value: unknown): string {
	if (value === null || value === undefined) return "";
	if (typeof value === "string") return value;
	if (
		typeof value === "number" ||
		typeof value === "boolean" ||
		typeof value === "bigint"
	) {
		return String(value);
	}
	return "";
}

/**
 * Mirrors legacy member-case PAP routing: which manufacturer PDF (`pdftype`) to use.
 * Types 6 / 12 are English vs Spanish Amgen-style forms (user picks language).
 */
export function resolvePapPdfOutcome(
	medicationShortName: unknown,
	medicalRetail: unknown
): PapPdfOutcome {
	const med = scalarToString(medicationShortName).toLowerCase();
	const retail = scalarToString(medicalRetail);

	if (med === "") {
		return { kind: "none" };
	}

	if (
		includesAny(med, AMGEN_PHARMACY) &&
		equalsIgnoreCase(retail, "pharmacy")
	) {
		return { kind: "language" };
	}
	if (includesAny(med, ["prolia"]) && equalsIgnoreCase(retail, "medical")) {
		return { kind: "language" };
	}
	if (includesAny(med, ABBVIE)) {
		return { kind: "language" };
	}
	if (includesAny(med, ["dupixent"])) {
		return { kind: "fixed", pdftype: 16 };
	}
	if (includesAny(med, GILEAD)) {
		return { kind: "fixed", pdftype: 15 };
	}
	if (includesAny(med, JANSSEN)) {
		return { kind: "fixed", pdftype: 20 };
	}
	if (includesAny(med, LILLY)) {
		return { kind: "fixed", pdftype: 21 };
	}
	if (includesAny(med, ["nucala"])) {
		return { kind: "fixed", pdftype: 22 };
	}
	if (includesAny(med, ["durolane"])) {
		return { kind: "fixed", pdftype: 23 };
	}
	if (includesAny(med, ["vivitrol"])) {
		return { kind: "fixed", pdftype: 24 };
	}
	if (includesAny(med, ["ocrevus"])) {
		return { kind: "fixed", pdftype: 26 };
	}
	if (includesAny(med, ["euflexxa"])) {
		return { kind: "fixed", pdftype: 25 };
	}
	if (includesAny(med, ["fintepla"])) {
		return { kind: "fixed", pdftype: 27 };
	}
	if (includesAny(med, ["inflectra", "ruxience"])) {
		return { kind: "fixed", pdftype: 28 };
	}
	if (includesAny(med, ["wegovy"])) {
		return { kind: "fixed", pdftype: 29 };
	}
	if (includesAny(med, ["ibrance"])) {
		return { kind: "fixed", pdftype: 30 };
	}
	if (includesAny(med, ["fasenra", "norditropin"])) {
		return { kind: "fixed", pdftype: 6 };
	}
	if (includesAny(med, ["sprycel"])) {
		return { kind: "fixed", pdftype: 32 };
	}
	if (includesAny(med, ["eylea"])) {
		return { kind: "fixed", pdftype: 33 };
	}
	if (includesAny(med, TYPE6_PAP)) {
		return { kind: "fixed", pdftype: 6 };
	}

	return { kind: "none" };
}
